import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

/**
 * New-tab-friendly in-app navigation.
 *
 * The problem: `onClick={() => navigate(path)}` renders no <a href>, so the
 * browser has nothing to open — Ctrl/Cmd/middle-click can't open the record in
 * a new tab. The fix is to make the clickable element a real anchor.
 *
 * Two ways to use this, depending on whether the click does anything besides
 * navigating:
 *
 * 1. PURE navigation (the common case) — the onClick only calls navigate():
 *    just render the element as a link. React Router's <Link> already renders a
 *    real <a href>, does SPA navigation on a plain click, and — crucially —
 *    lets Ctrl/Cmd/Shift/middle clicks fall through so the browser opens a new
 *    tab natively. No custom handler needed:
 *
 *        <Button component={RouterLink} to={`/finance/journal/${id}`}>View</Button>
 *
 *    Re-export of RouterLink is provided here so a page needs only one import.
 *
 * 2. Navigation WITH side effects (stopPropagation, closing a menu, etc.) — use
 *    `linkNav(to, navigate, extra?)`. It returns props to spread onto any MUI
 *    clickable so it becomes a real <a href> that opens a new tab on a modified
 *    click, while a plain click runs the side effects and then SPA-navigates:
 *
 *        <IconButton {...linkNav(`/finance/journal/${id}`, navigate, e => e.stopPropagation())}>
 */

export { RouterLink };

/** A click carrying a browser "open elsewhere" intent (new tab / window). */
export const isModifiedClick = (e: React.MouseEvent) =>
  e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e as any).button === 1;

export function linkNav(
  to: string,
  navigate: (to: string) => void,
  extra?: (e: React.MouseEvent) => void,
) {
  return {
    component: RouterLink,
    to,
    onClick: (e: React.MouseEvent) => {
      // Let the browser open a new tab/window natively.
      if (isModifiedClick(e)) return;
      // Plain click: this is an in-app SPA navigation, so stop the anchor's
      // own full-page navigation, run any side effects, then route.
      e.preventDefault();
      extra?.(e);
      navigate(to);
    },
  } as any;
}
